import React, { useEffect } from "react";
import { useSnackbar } from '../contexts/SnackbarProvider';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Checkbox, Switch, Stack
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axiosInstance from "../utils/axiosInstance";

const SupplierContactModal = ({ open, onClose, contact, supplierId, onSaved, onDeleted }) => {
    const { showSnackbar } = useSnackbar();
    const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            role: "",
            is_primary: false,
            status: "active"
        },
    });

    // 初期値セット
    useEffect(() => {
        if (contact) {
            reset({
                name: contact.name || "",
                email: contact.email || "",
                phone: contact.phone || "",
                role: contact.role || "",
                is_primary: contact.is_primary || false,
                status: contact.status || "active",
            });
        } else {
            reset({
                name: "",
                email: "",
                phone: "",
                role: "",
                is_primary: false,
                status: "active",
            });
        }
    }, [contact, open, reset]);

    const onSubmit = async (data) => {
        try {
            let res;
            if (contact) {
                res = await axiosInstance.put(`/supplier/contact/${contact.supplier_contact_id}`, {
                    ...data,
                    supplier_id: supplierId,
                });
            } else {
                res = await axiosInstance.post(`/supplier/contact`, {
                    ...data,
                    supplier_id: supplierId,
                });
            }
            onSaved(res.data);
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '保存に失敗しました', 'error');
        }
    };

    const handleDelete = async () => {
        if (!contact) return;
        if (!window.confirm("担当者を削除しますか？")) return;

        try {
            const payload = {
                ...contact,
                status: "invalid",
            };

            const res = await axiosInstance.put(
                `/supplier/contact/${contact.supplier_contact_id}`,
                payload
            );

            onDeleted && onDeleted(contact.supplier_contact_id);
            showSnackbar(res.data?.message || '削除しました', 'success');
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{contact ? "担当者編集" : "担当者追加"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: "名前は必須です", maxLength: { value: 50, message: "50文字以内で入力してください" } }}
                        render={({ field }) => (
                            <TextField
                                label="名前"
                                {...field}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                inputProps={{ maxLength: 50 }}
                            />
                        )}
                    />
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: "メールアドレスは必須です",
                            maxLength: { value: 255, message: "255文字以内で入力してください" },
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "メール形式が正しくありません" }
                        }}
                        render={({ field }) => (
                            <TextField
                                label="メールアドレス"
                                {...field}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                inputProps={{ maxLength: 255 }}
                            />
                        )}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        rules={{
                            maxLength: { value: 20, message: "20文字以内で入力してください" },
                            pattern: { value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/, message: "電話番号形式が正しくありません" }
                        }}
                        render={({ field }) => (
                            <TextField
                                label="電話番号"
                                {...field}
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                                inputProps={{ maxLength: 20 }}
                            />
                        )}
                    />
                    <Controller
                        name="role"
                        control={control}
                        rules={{ maxLength: { value: 50, message: "50文字以内で入力してください" } }}
                        render={({ field }) => <TextField label="役割" fullWidth {...field} inputProps={{ maxLength: 50 }} />}
                    />
                    <Controller
                        name="is_primary"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Checkbox checked={field.value} {...field} />}
                                label="メイン担当"
                            />
                        )}
                    />
                    {contact && (
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={field.value === "active"}
                                            onChange={(e) =>
                                                field.onChange(e.target.checked ? "active" : "pending")
                                            }
                                        />
                                    }
                                    label="有効"
                                />
                            )}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                {contact && (
                    <Button color="error" onClick={handleDelete} disabled={isSubmitting} variant="outlined" sx={{ mr: 'auto' }}>
                        削除する
                    </Button>
                )}
                <Button onClick={onClose} disabled={isSubmitting} variant="outlined">キャンセル</Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
                    保存
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SupplierContactModal;